/**
 * The changed-fields save (piece 13; decision 3062). A form's Save sends the
 * fields whose value differs from what the page read, and nothing else. The
 * audit history then names only those, and two people saving one record at
 * once no longer write each other's untouched fields back over the other's.
 *
 * 🔨 Empty is one value: a field read as nothing and drawn as an empty box is
 * unchanged, whether the read said null, left the key out, or said ''.
 */
export function sameFieldValue(read: unknown, drawn: unknown): boolean {
  const empty = (value: unknown) => value === null || value === undefined || value === '';
  if (empty(read) && empty(drawn)) return true;
  if (Array.isArray(read) || Array.isArray(drawn)) {
    if (!Array.isArray(read) || !Array.isArray(drawn) || read.length !== drawn.length) return false;
    return read.every((value, index) => sameFieldValue(value, drawn[index]));
  }
  if (typeof read === 'object' && read !== null && typeof drawn === 'object' && drawn !== null) {
    const keys = new Set([...Object.keys(read), ...Object.keys(drawn)]);
    return [...keys].every((key) => sameFieldValue((read as Record<string, unknown>)[key], (drawn as Record<string, unknown>)[key]));
  }
  return Object.is(read, drawn);
}

/**
 * The fields of a draft whose value differs from what the page read, with the
 * drafted value. 🔨 A key the draft carries and the read does not is a change
 * where it holds something; a key the read carries and the draft does not is
 * untouched and never sent.
 */
export function changedFields(read: Readonly<Record<string, unknown>>, draft: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(draft).filter(([key, value]) => !sameFieldValue(read[key], value)));
}

/** Whether anything in the draft differs from what the page read: what the leave guard reads on a registry form. */
export function hasChangedFields(read: Readonly<Record<string, unknown>>, draft: Readonly<Record<string, unknown>>): boolean {
  return Object.keys(changedFields(read, draft)).length > 0;
}
